import type { FilterInterface } from '@shopify/polaris';
import type { TemplateType } from '~/types/template';

import { FILTER_PROVIDER_SOURCE, useAssetTemplateContext } from '~/modules/asset-template';
import { Badge, ChoiceList, Filters, InlineStack } from '@shopify/polaris';
import { FILTER_DISPLAY_MODE, FILTER_TEMPLATE_STATUS } from '../hooks';
import { FILTER_TAB_TEMPLATES } from '~/constants/template';
import { useAssetTemplatePickerContext } from '../contexts';
import { ITemplateStatusFilter } from '../types';
import { useTranslation } from 'react-i18next';

export interface AssetTemplateFilterControlProps {
  children?: React.ReactNode;
}

export const AssetTemplateFilterControl: React.FC<AssetTemplateFilterControlProps> = (props) => {
  const { t } = useTranslation();
  const {
    pageTypes,
    providerTab,
    queryValue,
    displayMode,
    status,
    appliedFilters,
    handleFiltersClearAll,
    handleQueryValueRemove,
    handleFiltersQueryChange,
    handlePageTypeChange,
    handleDisplayModeChange,
    handleProviderTabChange,
    handleStatusChange,
  } = useAssetTemplatePickerContext();
  const { isLoadingAsset, modalAssetTemplate } = useAssetTemplateContext();
  const { modalData, isPickVariant } = modalAssetTemplate;

  const filters: FilterInterface[] = [
    {
      key: 'pageType',
      label: t('Page type'),
      disabled: isPickVariant && !modalData?.allowTestingBetweenOtherPageType,
      filter: (
        <ChoiceList
          title={t('Page type')}
          titleHidden
          allowMultiple
          choices={FILTER_TAB_TEMPLATES}
          selected={pageTypes || []}
          onChange={(selected: TemplateType[]) => handlePageTypeChange(selected)}
        />
      ),
      pinned: true,
    },
    {
      key: 'status',
      label: t('Status'),
      filter: (
        <ChoiceList
          title={t('Status')}
          titleHidden
          allowMultiple
          choices={FILTER_TEMPLATE_STATUS.map((item) => {
            const isStatusFromGP =
              item.value === ITemplateStatusFilter.DRAFT ||
              item.value === ITemplateStatusFilter.NEED_REPUBLISHING;
            return {
              value: item.value,
              label: isStatusFromGP ? (
                <InlineStack gap="150" blockAlign="center">
                  {item.label} <Badge>{t('from GemPages')}</Badge>
                </InlineStack>
              ) : (
                item.label
              ),
            };
          })}
          selected={status || []}
          onChange={(selected: ITemplateStatusFilter[]) => handleStatusChange(selected)}
        />
      ),
      pinned: true,
    },
    {
      key: 'providerTab',
      label: t('Template source'),
      filter: (
        <ChoiceList
          title={t('Template source')}
          titleHidden
          allowMultiple
          choices={FILTER_PROVIDER_SOURCE}
          selected={providerTab}
          onChange={handleProviderTabChange}
        />
      ),
      pinned: true,
    },
    {
      key: 'displayMode',
      label: t('Display: All templates'),
      filter: (
        <ChoiceList
          title={t('Display')}
          titleHidden
          allowMultiple
          choices={FILTER_DISPLAY_MODE}
          selected={displayMode || []}
          onChange={handleDisplayModeChange}
        />
      ),
      pinned: true,
    },
  ];

  // TODO: Recheck;
  // useEffect(() => {
  //   if (!isPickVariant || typeof pageTypes !== 'undefined') return;
  //   const variantA = findVariantByType(modalData?.campaign, IVariantType.VARIANT_A);
  //   const variantB = findVariantByType(modalData?.campaign, IVariantType.VARIANT_B);
  //   if (!variantA && !variantB) return;
  //   const templateType = parseTemplateInfoBy({
  //     templateSuffix: variantB?.templateSuffix || variantA?.templateSuffix,
  //   })?.type;
  //   if (!templateType) return;
  //   handlePageTypeChange([templateType]);
  // }, [modalData?.campaign, pageTypes, handlePageTypeChange, isPickVariant]);

  return (
    <div className="gx-template-filters">
      <Filters
        closeOnChildOverlayClick
        children={props.children}
        queryValue={queryValue}
        filters={filters}
        disabled={isLoadingAsset}
        appliedFilters={appliedFilters}
        queryPlaceholder={t('Searching all templates')}
        onQueryChange={handleFiltersQueryChange}
        onQueryClear={handleQueryValueRemove}
        onClearAll={handleFiltersClearAll}
      />
    </div>
  );
};
